import React, { useState, useCallback, useMemo, useEffect, memo } from 'react';
import { useFormik } from 'formik';
import { useDropzone } from 'react-dropzone';
import { not, path, pathEq, pipe, pick, pathOr, slice } from 'ramda';
import PropTypes from 'prop-types';

import {
  useInfoItemsActions,
  useAlerts,
  ID,
  MESSAGE,
  META,
  FILES,
  EDITOR,
  OWNER,
  MODIFIED,
  CREATED,
  TAGS,
  SUB_PATIENT,
  FILE_ID,
  FILE_URL,
  FILE_URL_EXPIRES,
  MIMETYPE,
  ORIGINAL_NAME,
  NAME,
} from 'store/infoItems';
import { FIRST_NAME, LAST_NAME } from 'store/session';
import Switcher from 'components/Switcher';
import ErrMessage from 'components/Message';
import Warn from 'components/WarnDeleting';
import Spinner from 'components/FullScreenSpinner';
import Close from 'assets/svg-icons/SmallClose';
import PlusIcon from 'assets/svg-icons/PlusDropZone';
import { parseDateWithTime } from 'utils';
import FileForm from '../FileForm';
import { DropZone } from '../styles';

import { Container, HeadWrap, MenuBtn, TextArea, Message, Text, EmptyMessage } from './styles';

const InfoItem = ({ data }) => {
  const [readMode, setReadMode] = useState(true);
  const [isShowWarn, setIsShowWarn] = useState(false);
  const handleChangeReadMode = useCallback(() => setReadMode(not), []);
  const { saveInfoItem, removeInfoItem, uploadMedia } = useInfoItemsActions();
  const { loading, error, extractId } = useAlerts();
  const uploadAlerts = useAlerts();

  const onSend = useCallback((values) => pipe(pick([ID, MESSAGE, FILES]), saveInfoItem)(values), [saveInfoItem]);

  const { values, setValues, getFieldProps } = useFormik({
    initialValues: data,
    validate: onSend,
    validateOnBlur: false,
    validateOnMount: false,
  });

  const meta = values[META];
  const editor = useMemo(() => {
    const edit = path([META, EDITOR], data);
    if (!edit) return '';
    return ['Zul. bearbeitet von', edit[FIRST_NAME], edit[LAST_NAME], 'am', parseDateWithTime(data[META][MODIFIED])]
      .filter(Boolean)
      .join(' ');
  }, [data]);
  const owner = useMemo(
    () =>
      ['Erstellt von', meta[OWNER][FIRST_NAME], meta[OWNER][LAST_NAME], 'am', parseDateWithTime(meta[CREATED])]
        .filter(Boolean)
        .join(' '),
    [meta]
  );

  const setFiles = useCallback(
    (arg) => setValues(($) => ({ ...$, [FILES]: typeof arg === 'function' ? arg($[FILES]) : arg })),
    [setValues]
  );

  const handleShowWarn = useCallback(() => setIsShowWarn(not), []);
  const handleDelete = useCallback(() => {
    extractId(removeInfoItem(data[ID]));
  }, [extractId, removeInfoItem, data]);

  const onDrop = useCallback((files) => uploadAlerts.extractId(uploadMedia(files)), [uploadAlerts, uploadMedia]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  useEffect(() => {
    if (error && isShowWarn) {
      setIsShowWarn(not);
    }
  }, [error, isShowWarn]);

  useEffect(() => {
    if (uploadAlerts.success) {
      setFiles(($) => [...$, ...uploadAlerts.success]);
      uploadAlerts.resetAlerts();
    }
  }, [uploadAlerts, setFiles, values]);

  return (
    <Container>
      <HeadWrap>
        <Switcher label="BEARBEITEN" checked={!readMode} onChange={handleChangeReadMode} allowChange />
      </HeadWrap>
      {readMode ? (
        <>
          {!values[FILES].length && !values[MESSAGE] && <EmptyMessage>Leerer Eintrag</EmptyMessage>}
          {values[MESSAGE] && <Message>{values[MESSAGE]}</Message>}
        </>
      ) : (
        <>
          <HeadWrap>
            <MenuBtn type="button" title="Löschen Sie" onClick={setIsShowWarn}>
              <Close />
            </MenuBtn>
          </HeadWrap>
          <TextArea placeholder="Keine Nachricht" {...getFieldProps(MESSAGE)} />
        </>
      )}
      {readMode || (
        <DropZone height="60px" mb="10px" isActive={isDragActive} {...getRootProps()}>
          {uploadAlerts.loading ? (
            <Spinner />
          ) : (
            <>
              <input {...getInputProps()} />
              <PlusIcon />
            </>
          )}
        </DropZone>
      )}
      {values[FILES].map((item) => (
        <FileForm key={item[FILE_ID]} fileData={item} setFiles={setFiles} readMode={readMode} attachments={values[FILES]} />
      ))}
      <Text>{editor}</Text>
      <Text>{owner}</Text>
      {isShowWarn && (
        <Warn
          loading={loading}
          message="Wollen Sie den Eintrag (inkl. Dateianhänge) löschen?"
          showActon={handleShowWarn}
          deleteAction={handleDelete}
        />
      )}
      {error && <ErrMessage type="error">{error}</ErrMessage>}
    </Container>
  );
};

InfoItem.propTypes = {
  data: PropTypes.shape({
    [ID]: PropTypes.string.isRequired,
    [SUB_PATIENT]: PropTypes.string.isRequired,
    [MESSAGE]: PropTypes.string,
    [FILES]: PropTypes.arrayOf(
      PropTypes.shape({
        [FILE_ID]: PropTypes.string.isRequired,
        [FILE_URL]: PropTypes.string,
        [FILE_URL_EXPIRES]: PropTypes.string,
        [MIMETYPE]: PropTypes.string.isRequired,
        [ORIGINAL_NAME]: PropTypes.string.isRequired,
        [NAME]: PropTypes.string,
        [TAGS]: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      })
    ).isRequired,
    [META]: PropTypes.shape({
      [OWNER]: PropTypes.shape({
        [FIRST_NAME]: PropTypes.string,
        [LAST_NAME]: PropTypes.string,
      }).isRequired,
      [EDITOR]: PropTypes.shape({
        [FIRST_NAME]: PropTypes.string,
        [LAST_NAME]: PropTypes.string,
      }),
      [CREATED]: PropTypes.string.isRequired,
      [MODIFIED]: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default memo(
  InfoItem,
  (prev, next) =>
    pathEq(path(['data', META, EDITOR, ID], next), ['data', META, EDITOR, ID], prev) &&
    pipe(pathOr('', ['data', META, MODIFIED]), slice(0, 16))(prev) ===
      pipe(pathOr('', ['data', META, MODIFIED]), slice(0, 16))(next)
);
